import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Search, CheckCircle2, XCircle, Award, Calendar, User } from 'lucide-react'
import { masterclassService } from '../services/masterclassService'
import api from '../services/api'
import CinematicNav from '../components/landing/CinematicNav'
import CinematicFooter from '../components/landing/CinematicFooter'
import Reveal from '../components/landing/Reveal'

export default function VerifyCertificatePage() {
  const [searchParams] = useSearchParams()
  const [number, setNumber] = useState('')
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const autoVerifiedRef = useRef(false)

  const isQrFlow = Boolean(searchParams.get('certificate') || searchParams.get('cert') || searchParams.get('number'))

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const verifyNumber = useCallback(async (certificateNumber) => {
    const value = certificateNumber.trim()
    if (!value) return

    setLoading(true)
    setResult(null)
    try {
      const { data } = await api.get(`/certificates/verify/${encodeURIComponent(value)}`)
      setResult({ valid: true, cert: data.certificate, type: 'program' })
    } catch {
      try {
        const data = await masterclassService.verifyCertificate(value)
        setResult({ valid: true, cert: data.certificate, type: 'masterclass' })
      } catch {
        setResult({ valid: false, number: value })
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (autoVerifiedRef.current) return
    const certificateNumber =
      searchParams.get('certificate') ||
      searchParams.get('cert') ||
      searchParams.get('number')
    if (!certificateNumber) return

    autoVerifiedRef.current = true
    setNumber(certificateNumber)
    verifyNumber(certificateNumber)
  }, [searchParams, verifyNumber])

  const handleVerify = (event) => {
    event.preventDefault()
    verifyNumber(number)
  }

  const certificateTitle = result?.cert?.course_title || result?.cert?.topic
  const certificateDate = result?.cert?.completion_date || result?.cert?.event_date || result?.cert?.issued_date

  return (
    <div className="cinema-page min-h-screen bg-cinema-black">
      <CinematicNav scrolled={scrolled} navOverHero={!scrolled} />

      <section className="pt-40 pb-24">
        <div className="cinema-container max-w-2xl">
          <Reveal>
            <span className="cinema-label">Certificate Verification</span>
            <h1 className="cinema-headline-lg mt-6 text-white">
              {isQrFlow ? 'Verifying Certificate' : 'Verify an SMA Certificate'}
            </h1>
            <p className="cinema-body mt-4 text-cinema-muted">
              {loading
                ? `Verifying ${number}…`
                : isQrFlow
                ? 'Certificate number was read from the QR code. Verification is complete below.'
                : 'Scan a certificate QR code for instant verification, or enter a certificate number below.'}
            </p>
          </Reveal>

          <Reveal delay={100}>
            <form onSubmit={handleVerify} className="mt-10">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cinema-muted" />
                <input
                  type="text"
                  value={number}
                  onChange={(event) => setNumber(event.target.value)}
                  placeholder="CERT-2026-0001"
                  className="w-full bg-cinema-graphite border border-white/10 text-white pl-12 pr-32 py-4 font-sans uppercase tracking-wider focus:outline-none focus:border-cinema-gold/40 transition-colors"
                />
                <button
                  type="submit"
                  disabled={loading || !number.trim()}
                  className="absolute right-2 top-1/2 -translate-y-1/2 cinema-btn-primary !py-2.5 !px-6 !text-xs disabled:opacity-50"
                >
                  {loading ? 'Verifying...' : 'Verify'}
                </button>
              </div>
            </form>
          </Reveal>

          {loading && (
            <Reveal delay={200}>
              <div className="mt-10 cinema-glass p-8 text-center">
                <div className="w-10 h-10 border-2 border-cinema-gold/30 border-t-cinema-gold rounded-full animate-spin mx-auto mb-4" />
                <p className="text-cinema-soft">Verifying certificate {number}…</p>
              </div>
            </Reveal>
          )}

          {result && !loading && (
            <Reveal delay={200}>
              <div className="mt-10 cinema-glass p-8">
                {result.valid ? (
                  <div className="text-center">
                    <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h2 className="font-display text-2xl text-white">Certificate is Valid</h2>
                    <div className="mt-6 space-y-3 text-left max-w-sm mx-auto">
                      <div className="flex items-center gap-3 text-cinema-soft">
                        <User className="w-4 h-4 text-cinema-gold" />
                        <span>{result.cert.student_name}</span>
                      </div>
                      {certificateTitle && (
                        <div className="flex items-center gap-3 text-cinema-soft">
                          <Award className="w-4 h-4 text-cinema-gold" />
                          <span>{certificateTitle}</span>
                        </div>
                      )}
                      {certificateDate && (
                        <div className="flex items-center gap-3 text-cinema-soft">
                          <Calendar className="w-4 h-4 text-cinema-gold" />
                          <span>{new Date(certificateDate).toLocaleDateString('en-UG', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                        </div>
                      )}
                      <div className="mt-4 pt-4 border-t border-white/10 text-center">
                        <p className="text-cinema-muted font-mono text-sm">{result.cert.certificate_number}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="font-display text-2xl text-white">Certificate is Invalid or Not Found</h2>
                    <p className="text-cinema-muted mt-2">
                      {result.number
                        ? `The certificate number ${result.number} could not be verified.`
                        : 'The certificate number could not be verified.'}
                    </p>
                  </div>
                )}
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <CinematicFooter contactEmail="synergymultimediaacademy@gmail.com" />
    </div>
  )
}
